// Catalog.ID Client — Recovery Sheet Scanning
// ============================================================================
// Decodes Recovery Sheet QR payloads from uploaded files (photos, scans and
// PDFs) and, where available, a live camera. File upload is the GUARANTEE:
// the iframed dashboard may not get camera permission, a desktop may have no
// camera at all, and any phone can take a photo that gets uploaded. The camera
// is the convenience on top. jsQR loads lazily in both paths, and pdf.js loads
// lazily on top of that — only when the file picked really is a PDF.
//
// Also provides the sheet collector UI used by fire drills (§14) and the
// login recovery path (§15 path B): scan sheets one by one until a quorum of
// distinct sheet types from one set is assembled, and `createLiveScanner` —
// the reusable camera+upload element the verification page (§6.4) is built
// on, which the collector can adopt later.

import { createElement } from '@catalog-shell';
import { decodeRecoveryCard, type RecoveryCardPayload } from '../crypto/recovery-card';
import { decodeResetCard, type ResetCardPayload } from '../crypto/reset-card';
import { cardTypeLetter } from './booklet/shared';
import { isPdfFile, PDF_MAX_PAGES_DEFAULT } from './pdf-qr-rules';

/** File types the PDF-aware upload controls accept. Both a MIME list and the
 *  extension, because some platforms report neither reliably on their own. */
export const SHEET_UPLOAD_ACCEPT = 'image/*,application/pdf,.pdf';

/** Minimal RGBA image — what `getImageData` returns, and all the helpers below
 *  need. Kept structural so the pure parts test without a canvas. */
interface RgbaImage {
    data: Uint8ClampedArray;
    width: number;
    height: number;
}

/** Extract a byte-mode QR payload from an image file. Throws with a
 *  user-facing message when no readable QR is found. */
export async function qrBytesFromImageFile(file: File): Promise<Uint8Array> {
    const { default: jsQR } = await import('jsqr');

    const bitmap = await createImageBitmap(file).catch(() => {
        throw new Error(`${file.name} is not a readable image`);
    });
    try {
        // Try native resolution first, then a downscale — huge photos can
        // defeat jsQR's locator, small scans lose modules when downscaled.
        for (const maxDim of [bitmap.width > 2000 ? 2000 : bitmap.width, 1200, 800]) {
            const img = drawToImageData(bitmap, maxDim);
            const result = jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' });
            if (result && result.binaryData.length > 0) return new Uint8Array(result.binaryData);
        }
    } finally {
        bitmap.close();
    }
    throw new Error(`No readable QR code found in ${file.name} — try a sharper, straight-on photo of the sheet`);
}

function drawToImageData(bitmap: ImageBitmap, maxDim: number): RgbaImage {
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx2d = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx2d) throw new Error('Canvas unavailable');
    // White ground first: a photo with an alpha channel (or a PNG screenshot of
    // a sheet) reads back as black where it is transparent.
    ctx2d.fillStyle = '#ffffff';
    ctx2d.fillRect(0, 0, w, h);
    ctx2d.drawImage(bitmap, 0, 0, w, h);
    return ctx2d.getImageData(0, 0, w, h);
}

export interface QrFileOptions {
    /** PDFs only: pages to render before giving up. Defaults to
     *  PDF_MAX_PAGES_DEFAULT — a whole Membership Kit, and no further. */
    maxPages?: number;
    /** PDFs only: what the flow is looking for, for the "not found" line. */
    sheetLabel?: string;
    /**
     * PDFs only: which QR counts. A Membership Kit holds several KINDS of QR —
     * the Account Unlock Sheet's comes before the Recovery Sheets — so a flow
     * that is about one kind says so here and the scan keeps turning pages
     * instead of returning the first QR it happens to meet. Must be
     * MAC-blind (magic only): a damaged sheet is still that kind of sheet.
     * Default: the first QR on any page wins.
     */
    accept?: (bytes: Uint8Array) => boolean;
}

/**
 * Extract a byte-mode QR payload from an uploaded file — a photo, a scan, or a
 * PDF. Images take the path above; a PDF is rendered page by page until a page
 * yields a QR. pdf.js is fetched only on the PDF branch (its own chunk), so an
 * upload of a photo costs nothing extra. Throws with a user-facing message.
 */
export async function qrBytesFromFile(file: File, options: QrFileOptions = {}): Promise<Uint8Array> {
    if (!isPdfFile(file)) return qrBytesFromImageFile(file);
    const { qrBytesFromPdfFile } = await import('./pdf-qr');
    return qrBytesFromPdfFile(file, {
        maxPages: options.maxPages ?? PDF_MAX_PAGES_DEFAULT,
        sheetLabel: options.sheetLabel,
        accept: options.accept,
    });
}

/** Decode a Recovery Sheet from an image file. Throws with a user-facing message. */
export async function decodeCardFromImageFile(file: File): Promise<RecoveryCardPayload> {
    return decodeRecoveryCard(await qrBytesFromImageFile(file));
}

/**
 * Decode an Account Unlock Sheet from an uploaded photo, scan or PDF (1-of-1 —
 * no collector). The sheet Settings hands out is a ONE-page PDF, so a PDF is
 * searched no further than its first page: anything else that person uploaded
 * is the wrong document and should be named as such rather than silently
 * scanned to the end. Throws with a user-facing message on scan failure or
 * wrong sheet type.
 */
export async function decodeResetCardFromFile(file: File): Promise<ResetCardPayload> {
    return decodeResetCard(await qrBytesFromFile(file, {
        maxPages: 1,
        sheetLabel: 'Account Unlock Sheet',
    }));
}

export interface CardCollector {
    element: HTMLElement;
    getCards(): RecoveryCardPayload[];
    /** Fires whenever the collected set changes. */
    onChange(cb: (cards: RecoveryCardPayload[]) => void): void;
}

/**
 * UI that collects Recovery Sheets via image upload until a quorum is
 * assembled. Enforces one-set/distinct-type rules incrementally with
 * actionable errors.
 */
export function createCardCollector(): CardCollector {
    const cards: RecoveryCardPayload[] = [];
    const listeners: ((cards: RecoveryCardPayload[]) => void)[] = [];
    const notify = (): void => listeners.forEach(cb => cb([...cards]));

    const element = createElement('div', { className: 'cid-card-collector' });

    const listEl = createElement('div');
    element.appendChild(listEl);

    const statusEl = createElement('p', { className: 'cid-pane-note', textContent: 'No sheets scanned yet.' });
    element.appendChild(statusEl);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    element.appendChild(errorEl);

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    // Images only, deliberately: a quorum is several DIFFERENT sheet types, and
    // the one file that would hold several of them is the kit PDF — which holds
    // every sheet of the set at once and is exactly what the split was meant to
    // prevent being kept together. Custodians upload their own sheet.
    fileInput.accept = 'image/*';
    fileInput.multiple = true;
    fileInput.id = 'cid-card-scan-file';
    fileInput.style.display = 'none';
    element.appendChild(fileInput);

    const actions = createElement('div', { className: 'cid-credential-actions' });
    const uploadBtn = createElement('button', {
        className: 'cs-btn cs-btn--default cid-credential-action',
        textContent: 'Add sheet from photo or scan…',
    });
    uploadBtn.addEventListener('click', () => fileInput.click());
    actions.appendChild(uploadBtn);
    element.appendChild(actions);

    function renderList(): void {
        listEl.innerHTML = '';
        for (const card of cards) {
            const row = createElement('div', { className: 'cid-key-field' });
            row.appendChild(createElement('span', {
                className: 'cid-key-field-label',
                textContent: `Sheet ${cardTypeLetter(card.shareIndex)}`
                    + `${card.cardLabel ? ` — ${card.cardLabel}` : ''} `,
            }));
            row.appendChild(createElement('span', {
                className: 'cid-key-field-value',
                textContent: `(set ${card.recoverySetId.slice(0, 8)}…, ${card.thresholdK} of ${card.totalSharesN})`,
            }));
            const removeBtn = createElement('button', {
                className: 'cs-btn cs-btn--default',
                textContent: 'Remove',
            });
            removeBtn.addEventListener('click', () => {
                cards.splice(cards.indexOf(card), 1);
                renderList();
                updateStatus();
                notify();
            });
            row.appendChild(removeBtn);
            listEl.appendChild(row);
        }
    }

    function updateStatus(): void {
        if (cards.length === 0) {
            statusEl.textContent = 'No sheets scanned yet.';
            return;
        }
        const need = cards[0].thresholdK;
        statusEl.textContent = cards.length >= need
            ? `Quorum assembled: ${cards.length} of ${need} required sheet types.`
            : `${cards.length} of ${need} required sheet types scanned — add ${need - cards.length} more.`;
    }

    fileInput.addEventListener('change', async () => {
        const files = Array.from(fileInput.files ?? []);
        fileInput.value = '';
        errorEl.classList.remove('cid-pane-error--visible');
        for (const file of files) {
            try {
                const payload = await decodeCardFromImageFile(file);
                if (cards.some(c => c.shareIndex === payload.shareIndex && c.recoverySetId === payload.recoverySetId)) {
                    throw new Error(`Sheet ${cardTypeLetter(payload.shareIndex)} is already scanned — `
                        + 'copies of the same sheet type do not count; scan a DIFFERENT type');
                }
                if (cards.length > 0 && cards[0].recoverySetId !== payload.recoverySetId) {
                    throw new Error('This sheet belongs to a different recovery set than the ones already scanned');
                }
                cards.push(payload);
            } catch (err) {
                errorEl.textContent = err instanceof Error ? err.message : `Could not read ${file.name}`;
                errorEl.classList.add('cid-pane-error--visible');
            }
        }
        renderList();
        updateStatus();
        notify();
    });

    return {
        element,
        getCards: () => [...cards],
        onChange: (cb) => { listeners.push(cb); },
    };
}

// ── Live camera scanner (§6.2) ───────────────────────────────────────────────
// The camera is the convenience path; upload is the guarantee. Everything that
// can be decided without touching the DOM is decided in the three pure
// functions below, so the fallback rules are testable without a webcam.

export type CameraFailure = 'denied' | 'no-camera' | 'insecure' | 'unsupported' | 'error';

/**
 * Pure: can this context even ask for a camera? `getUserMedia` is undefined in
 * an insecure context in some engines and merely throws in others, so the
 * unsupported answer wins when both are false — there is nothing to ask
 * permission of.
 */
export function cameraAvailability(
    env: { hasGetUserMedia: boolean; isSecureContext: boolean },
): 'ok' | 'insecure' | 'unsupported' {
    if (!env.hasGetUserMedia) return 'unsupported';
    if (!env.isSecureContext) return 'insecure';
    return 'ok';
}

/** Pure: map a getUserMedia rejection onto the reason we tell the custodian. */
export function classifyCameraError(err: unknown): CameraFailure {
    const name = err instanceof Error ? err.name : '';
    switch (name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
        case 'SecurityError':
            return 'denied';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
        case 'OverconstrainedError':
            return 'no-camera';
        default:
            return 'error';
    }
}

/** Pure: the single line shown above the upload control when the camera is out. */
export function cameraFallbackMessage(reason: CameraFailure): string {
    switch (reason) {
        case 'denied':
            return 'Camera permission was refused — upload a photo or scan of the sheet instead.';
        case 'no-camera':
            return 'No camera was found on this device — upload a photo or scan of the sheet instead.';
        case 'insecure':
            return 'The camera needs a secure (https or localhost) connection — upload a photo or scan of the sheet instead.';
        case 'unsupported':
            return 'This browser offers no camera access here — upload a photo or scan of the sheet instead.';
        case 'error':
            return 'The camera could not be started — upload a photo or scan of the sheet instead.';
    }
}

const CAMERA_START_LABEL = 'Scan with the camera';
const CAMERA_STOP_LABEL = 'Stop the camera';

export interface LiveScannerOptions {
    /**
     * Fires with the RAW QR payload bytes the moment a code decodes, camera
     * already stopped. Raw bytes rather than a decoded card on purpose: the
     * verification page must fingerprint exactly what it scanned (§6.2) and
     * must survive a payload that fails its MAC, both of which a pre-decoded
     * payload would hide.
     */
    onDecoded(bytes: Uint8Array): void;
    /** Label on the upload control. */
    uploadLabel?: string;
    /** Milliseconds between frame grabs. */
    intervalMs?: number;
    /** PDF uploads: page cap, sheet label and QR filter, forwarded verbatim to
     *  `qrBytesFromFile`. Irrelevant to the camera and to image uploads. */
    pdf?: QrFileOptions;
}

export interface LiveScanner {
    element: HTMLElement;
    /**
     * Ask for the camera and begin scanning. Falls back to upload on any refusal.
     * The control's own camera button calls it; a page never calls it on paint,
     * because asking raises the browser's camera prompt, and that is for someone
     * who chose the camera.
     */
    start(): Promise<void>;
    /** Stop scanning and release the camera. Idempotent — safe to call on teardown. */
    stop(): void;
    /**
     * Go back to scanning after a decode the page did not take: the camera
     * again if it was what decoded, otherwise nothing — an upload is picked
     * again by hand, and must not turn into a camera prompt.
     */
    resume(): Promise<void>;
}

/**
 * A reusable scan control: live camera where it is available, image upload
 * always. Used by the Recovery Sheet verification page (§6.4) and the
 * registration entry page; the login recovery collector can adopt it later
 * without changing its own behaviour. The camera starts from its button, never
 * by itself.
 */
export function createLiveScanner(options: LiveScannerOptions): LiveScanner {
    const intervalMs = options.intervalMs ?? 250;
    const element = createElement('div', { className: 'cid-sheet-scanner' });

    const video = document.createElement('video');
    video.className = 'cid-sheet-scanner-video';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');   // iOS Safari honours the attribute
    video.setAttribute('muted', '');
    video.style.display = 'none';
    element.appendChild(video);

    const hint = createElement('p', { className: 'cid-pane-note cid-sheet-scanner-hint' });
    hint.style.display = 'none';
    hint.textContent = 'Hold the sheet so the QR code fills the frame.';
    element.appendChild(hint);

    const note = createElement('p', { className: 'cid-pane-note cid-sheet-scanner-note' });
    note.style.display = 'none';
    element.appendChild(note);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    element.appendChild(errorEl);

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    // A custodian who was sent the Membership Kit as a PDF should be able to
    // hand that file straight over, rather than printing it to photograph it.
    fileInput.accept = SHEET_UPLOAD_ACCEPT;
    fileInput.style.display = 'none';
    element.appendChild(fileInput);

    const actions = createElement('div', { className: 'cid-credential-actions' });
    const cameraBtn = createElement('button', {
        className: 'cs-btn cs-btn--default cid-credential-action',
        textContent: CAMERA_START_LABEL,
    });
    cameraBtn.addEventListener('click', () => {
        if (stream) stop();
        else void start();
    });
    actions.appendChild(cameraBtn);
    const uploadBtn = createElement('button', {
        className: 'cs-btn cs-btn--default cid-credential-action',
        textContent: options.uploadLabel ?? 'Upload a photo, scan or PDF of the sheet…',
    });
    uploadBtn.addEventListener('click', () => fileInput.click());
    actions.appendChild(uploadBtn);
    element.appendChild(actions);

    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let canvas: HTMLCanvasElement | null = null;
    let stopped = false;
    /** Was the camera what the last decode came from? {@link LiveScanner.resume} asks. */
    let resumeCamera = false;
    let starting = false;

    function availabilityNow(): 'ok' | 'insecure' | 'unsupported' {
        return cameraAvailability({
            hasGetUserMedia: typeof navigator !== 'undefined'
                && typeof navigator.mediaDevices?.getUserMedia === 'function',
            isSecureContext: typeof window !== 'undefined' ? window.isSecureContext !== false : false,
        });
    }

    function showFallback(reason: CameraFailure): void {
        note.textContent = cameraFallbackMessage(reason);
        note.style.display = '';
        video.style.display = 'none';
        hint.style.display = 'none';
    }

    function stop(): void {
        stopped = true;
        if (timer !== null) { clearInterval(timer); timer = null; }
        if (stream) {
            // Release the device itself, not just the element's reference —
            // a live track keeps the camera light on.
            for (const track of stream.getTracks()) track.stop();
            stream = null;
        }
        try { video.pause(); } catch { /* not playing */ }
        video.srcObject = null;
        video.style.display = 'none';
        hint.style.display = 'none';
        cameraBtn.textContent = CAMERA_START_LABEL;
    }

    function deliver(bytes: Uint8Array): void {
        resumeCamera = stream !== null;
        stop();
        options.onDecoded(bytes);
    }

    async function start(): Promise<void> {
        // One camera at a time: a second press while the prompt is up would open
        // a second stream and orphan the first, camera light on, nothing to stop it.
        if (starting || stream) return;
        starting = true;
        cameraBtn.disabled = true;
        try {
            await startCamera();
        } finally {
            starting = false;
            cameraBtn.disabled = false;
        }
    }

    async function startCamera(): Promise<void> {
        stopped = false;
        errorEl.classList.remove('cid-pane-error--visible');

        const availability = availabilityNow();
        if (availability !== 'ok') {
            showFallback(availability);
            return;
        }

        let jsQR: typeof import('jsqr').default;
        try {
            ({ default: jsQR } = await import('jsqr'));
        } catch {
            showFallback('error');
            return;
        }

        try {
            stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' },
            });
        } catch (err) {
            showFallback(classifyCameraError(err));
            return;
        }

        // A stop() that raced the permission prompt must not leave the camera on.
        if (stopped) { for (const t of stream.getTracks()) t.stop(); stream = null; return; }

        note.style.display = 'none';
        video.srcObject = stream;
        video.style.display = '';
        hint.style.display = '';
        cameraBtn.textContent = CAMERA_STOP_LABEL;
        try {
            await video.play();
        } catch {
            // Autoplay refused (rare with a muted, inline video) — the frames
            // would stay black, so send the custodian to the upload control.
            stop();
            showFallback('error');
            return;
        }

        canvas ??= document.createElement('canvas');
        timer = setInterval(() => {
            const bytes = grabFrame(jsQR, video, canvas!);
            if (bytes) deliver(bytes);
        }, intervalMs);
    }

    fileInput.addEventListener('change', async () => {
        const file = fileInput.files?.[0];
        fileInput.value = '';
        if (!file) return;
        errorEl.classList.remove('cid-pane-error--visible');
        try {
            deliver(await qrBytesFromFile(file, options.pdf ?? {}));
        } catch (err) {
            errorEl.textContent = err instanceof Error ? err.message : `Could not read ${file.name}`;
            errorEl.classList.add('cid-pane-error--visible');
        }
    });

    async function resume(): Promise<void> {
        if (!resumeCamera) return;
        resumeCamera = false;
        await start();
    }

    // Where no camera can be asked for, there is no button to press: say why
    // up front, without asking anything — this is capability, not permission.
    const availability = availabilityNow();
    if (availability !== 'ok') {
        cameraBtn.style.display = 'none';
        showFallback(availability);
    }

    return { element, start, stop, resume };
}

/** One camera frame → QR payload bytes, or null when nothing decoded yet.
 *  Kept small and side-effect free apart from the shared scratch canvas. */
function grabFrame(
    jsQR: typeof import('jsqr').default,
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
): Uint8Array | null {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;   // metadata not in yet

    // Cap the working size: a 4K front camera costs far more per frame than it
    // adds in modules, and jsQR's locator does not benefit past ~800px.
    const scale = Math.min(1, 800 / Math.max(vw, vh));
    const w = Math.max(1, Math.round(vw * scale));
    const h = Math.max(1, Math.round(vh * scale));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const result = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
    return result && result.binaryData.length > 0 ? new Uint8Array(result.binaryData) : null;
}
